import React, { useState, useEffect, useCallback } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import axios from "axios";
import './Map.css';
import { useAuth } from "./AuthContext"; // <-- Import the Auth context

const containerStyle = {
  width: "800px",
  height: "600px",
};

const defaultCenter = {
  lat: 51.5473,
  lng: -0.0131,
};

const BASE_URL = process.env.REACT_APP_API_URL || "https://backend.whereisfran.com";
const LOCATION_URL = `${BASE_URL}/location`;

// Display names keyed by email address
const USER_NAMES = {
  "john.milsom01@gmail.com": "John",
  "liamdcunliffe@gmail.com": "Liam",
  "trevorgiles@gmail.com": "Trevor",
};

function Map() {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyBjS5PxGc8Ga1HqY0VwtXIQdrqxOEtiWxE",
  });

  const { idToken } = useAuth(); // <-- Get the token from context

  const [markerPosition, setMarkerPosition] = useState(null);
  const [clickedPosition, setClickedPosition] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  // Load stored location on page load
  useEffect(() => {
    if (!idToken) return; // Wait for token
    axios
      .get(LOCATION_URL, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      })
      .then((response) => {
        const { lat, lng, timestamp, updatedBy: email } = response.data;
        setMarkerPosition({ lat, lng });
        setLastUpdated(timestamp);
        setUpdatedBy(email || null);
      })
      .catch((error) => {
        console.error("Error fetching location:", error);
      });
  }, [idToken]);

  // Shared function to update location and save to API
  const updateLocationFromClicked = useCallback(
    (location) => {
      setMarkerPosition(location);

      // Save to API
      axios
        .post(LOCATION_URL, location, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        })
        .then((response) => {
          console.log("Location saved to API");
          if (response.data && response.data.timestamp) {
            setLastUpdated(response.data.timestamp);
          }
          if (response.data && response.data.updatedBy) {
            setUpdatedBy(response.data.updatedBy);
          }
          setClickedPosition(null); // Clear clicked position after saving
        })
        .catch((error) => console.error("Error saving location:", error));
    },
    [idToken]
  );

  const updateLocationFromCurrent = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log("Latitude:", latitude);
          console.log("Longitude:", longitude);
          const newLocation = { lat: latitude, lng: longitude };
          updateLocationFromClicked(newLocation);
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  }, [updateLocationFromClicked]);

  // Format a Firestore timestamp into a readable string e.g. "10 Sep 2026, 11:27 pm"
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Never";
    try {
      // Firestore REST timestamps come as { _seconds, _nanoseconds }
      // POST responses return a plain JS Date serialised as an ISO string
      const date = timestamp._seconds
        ? new Date(timestamp._seconds * 1000)
        : new Date(timestamp);
      return date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return String(timestamp);
    }
  };

  // Resolve an email address to a display name, falling back to the email itself
  const formatUpdatedBy = (email) => {
    if (!email) return null;
    return USER_NAMES[email] || email;
  };

  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <div className="map-container">
      <button
        onClick={updateLocationFromCurrent}
        className="default-btn"
      >
        Update with current location
      </button>
      {clickedPosition && (
        <button
          onClick={() => updateLocationFromClicked(clickedPosition)}
          className="default-btn"
        >
          Set location to clicked marker
        </button>
      )}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={markerPosition || defaultCenter}
          zoom={markerPosition ? 16 : 4}
          onClick={(e) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            setClickedPosition({ lat, lng });
            console.log("Map clicked at:", { lat, lng });
          }}
        >
          {markerPosition && (
            <Marker position={markerPosition}
              icon={{
                url: "/car.png",
                scaledSize: new window.google.maps.Size(40, 40),
              }} />
          )}
          {clickedPosition && (
            <Marker position={clickedPosition} />
          )}
        </GoogleMap>
      </div>
      <div className="last-updated">
        Last updated: {formatTimestamp(lastUpdated)}{formatUpdatedBy(updatedBy) ? ` by ${formatUpdatedBy(updatedBy)}` : ""}
      </div>
    </div>
  );
}

export default Map;